import { Button, Flex, MenuItem, Select, Table, Typography, Switch } from "flux";
import Page from "@adora/shared/components/layout/page";
import useExperimentCreatives, {
  ExperimentCreative,
  ExperimentCreativeUpdateRequest
} from "../../hooks/use-experiment-creatives";
import { useParams } from "react-router-dom";
import useBrandName from "../../hooks/use-brand-name";
import useExperiments from "../../hooks/use-experiments";
import { useMemo, useState } from "react";
import { CommandBarWrapper } from "@adora/shared/components/command-bar/command-bar-wrapper";

interface ExperimentCreativeFilters {
  isBlob: boolean | null;
  isWrongShoe: boolean | null;
  isIncoherent: boolean | null;
  isAcceptedAuto: boolean | null;
  isBlobManual: boolean | null;
  isWrongShoeManual: boolean | null;
  isIncoherentManual: boolean | null;
  isAcceptedManual: boolean | null;
  isBadLacesManual: boolean | null;
  isBadFineDetailsManual: boolean | null;
}

const ExperimentCreativesPage: React.FC = () => {
  const { brandId, experimentId } = useParams<{ brandId: string; experimentId: string }>();
  const brandName = useBrandName(brandId);
  const { experiments } = useExperiments(Number(brandId));
  const {
    creatives,
    isLoading,
    error,
    update: updateCreative
  } = useExperimentCreatives(Number(brandId), Number(experimentId));
  const [filters, setFilters] = useState<ExperimentCreativeFilters>({
    isBlob: null,
    isWrongShoe: null,
    isIncoherent: null,
    isAcceptedAuto: null,
    isBlobManual: null,
    isWrongShoeManual: null,
    isIncoherentManual: null,
    isAcceptedManual: null,
    isBadLacesManual: null,
    isBadFineDetailsManual: null
  });
  const [isLessTitles, setIsLessTitles] = useState(false);

  const handleToggleStatus = async (
    creativeId: number,
    field: keyof ExperimentCreativeUpdateRequest,
    currentValue: boolean | null
  ) => {
    const updateData: ExperimentCreativeUpdateRequest = {
      [field]: currentValue !== true
    };
    updateCreative(creativeId, updateData);
  };

  const experiment = experiments.find((exp) => exp.id === Number(experimentId));

  const columns = [
    { field: "id", value: "ID", width: 80, showInLessTitles: false },
    {
      field: "image",
      value: "Image",

      width: 300,
      renderCell: (creative: ExperimentCreative) => (
        <img
          src={creative.img_url}
          alt={`Creative ${creative.id}`}
          style={{ width: "100%", maxHeight: "600px", objectFit: "contain" }}
        />
      )
    },
    {
      field: "is_blob",
      value: "Blob",
      showInLessTitles: false,
      width: 100,
      renderCell: (creative: ExperimentCreative) => (creative.is_blob === null ? "-" : String(creative.is_blob))
    },
    {
      field: "is_wrong_shoe",
      value: "Wrong Shoe",
      width: 100,
      renderCell: (creative: ExperimentCreative) =>
        creative.is_wrong_shoe === null ? "-" : String(creative.is_wrong_shoe)
    },
    {
      field: "is_incoherent",
      value: "Incoherent",
      width: 100,
      renderCell: (creative: ExperimentCreative) =>
        creative.is_incoherent === null ? "-" : String(creative.is_incoherent)
    },
    {
      field: "is_accepted_auto",
      value: "Auto Accepted",
      showInLessTitles: false,

      width: 120,
      renderCell: (creative: ExperimentCreative) =>
        creative.is_accepted_auto === null ? "-" : String(creative.is_accepted_auto)
    },
    {
      field: "is_accepted_manual",
      value: "Manual Accepted",
      showInLessTitles: false,

      width: 140,
      renderCell: (creative: ExperimentCreative) =>
        creative.is_accepted_manual === null ? "-" : String(creative.is_accepted_manual)
    },
    {
      field: "actions",
      value: "Manual Review",
      width: 250,
      renderCell: (creative: ExperimentCreative) => (
        <Flex
          gap="8px"
          sx={{
            "@media (max-width: 2000px)": {
              flexDirection: "column",
              alignItems: "flex-start"
            }
          }}
          alignItems="stretch"
        >
          <Button
            variant={creative.is_blob_manual ? "contained" : "outlined"}
            color={creative.is_blob_manual ? "primary" : "secondary"}
            size="medium"
            onClick={() => handleToggleStatus(creative.id, "is_blob_manual", creative.is_blob_manual)}
            dataDogIdentifier="experiment creatives page blob"
          >
            Blob
          </Button>
          <Button
            variant={creative.is_wrong_shoe_manual ? "contained" : "outlined"}
            color={creative.is_wrong_shoe_manual ? "primary" : "secondary"}
            size="medium"
            onClick={() => handleToggleStatus(creative.id, "is_wrong_shoe_manual", creative.is_wrong_shoe_manual)}
            dataDogIdentifier="experiment creatives page wrong shoe"
          >
            Wrong Shoe
          </Button>
          <Button
            variant={creative.is_incoherent_manual ? "contained" : "outlined"}
            color={creative.is_incoherent_manual ? "primary" : "secondary"}
            size="medium"
            onClick={() => handleToggleStatus(creative.id, "is_incoherent_manual", creative.is_incoherent_manual)}
            dataDogIdentifier="experiment creatives page incoherent"
          >
            Incoherent
          </Button>
          <Button
            variant={creative.is_bad_laces_manual ? "contained" : "outlined"}
            color={creative.is_bad_laces_manual ? "primary" : "secondary"}
            size="medium"
            onClick={() => handleToggleStatus(creative.id, "is_bad_laces_manual", creative.is_bad_laces_manual)}
            dataDogIdentifier="experiment creatives page bad laces"
          >
            Bad Laces
          </Button>
          <Button
            variant={creative.is_bad_fine_details_manual ? "contained" : "outlined"}
            color={creative.is_bad_fine_details_manual ? "primary" : "secondary"}
            size="medium"
            onClick={() =>
              handleToggleStatus(creative.id, "is_bad_fine_details_manual", creative.is_bad_fine_details_manual)
            }
            dataDogIdentifier="experiment creatives page fine details"
          >
            Fine Details
          </Button>
        </Flex>
      )
    }
  ].filter((column) => (!isLessTitles ? true : column.showInLessTitles !== false));

  const experimentName = experiment ? experiment.name : `Experiment ${experimentId}`;
  const pageTitle = `Images - ${experimentName}`;

  const filteredCreatives = useMemo(() => {
    return creatives.filter((creative) => {
      if (filters.isBlob !== null && creative.is_blob !== filters.isBlob) return false;
      if (filters.isWrongShoe !== null && creative.is_wrong_shoe !== filters.isWrongShoe) return false;
      if (filters.isIncoherent !== null && creative.is_incoherent !== filters.isIncoherent) return false;
      if (filters.isAcceptedAuto !== null && creative.is_accepted_auto !== filters.isAcceptedAuto) return false;
      if (filters.isAcceptedManual !== null && creative.is_accepted_manual !== filters.isAcceptedManual) return false;
      if (filters.isBlobManual !== null && creative.is_blob_manual !== filters.isBlobManual) return false;
      if (filters.isWrongShoeManual !== null && creative.is_wrong_shoe_manual !== filters.isWrongShoeManual)
        return false;
      if (filters.isIncoherentManual !== null && creative.is_incoherent_manual !== filters.isIncoherentManual)
        return false;
      if (filters.isBadLacesManual !== null && creative.is_bad_laces_manual !== filters.isBadLacesManual) return false;
      if (
        filters.isBadFineDetailsManual !== null &&
        creative.is_bad_fine_details_manual !== filters.isBadFineDetailsManual
      )
        return false;
      return true;
    });
  }, [creatives, filters]);

  const handleFilterChange = (type: keyof ExperimentCreativeFilters, value: boolean | null) => {
    setFilters((prev) => ({ ...prev, [type]: value }));
  };

  return (
    <Page backLinkText={brandName} title={pageTitle}>
      {error ? (
        <div>Error: {error.message || "Failed to load experiment creatives. Please try again later."}</div>
      ) : (
        <>
          <CommandBarWrapper>
            {/* First row - Auto filters */}
            <div style={{ display: "grid", gridTemplateRows: "auto auto", gap: "12px" }}>
              <Flex gap="4px" pb="8px">
                <Flex alignItems="center" sx={{ minWidth: "124px" }}>
                  <Typography variant="subtitle1">Auto Filters:</Typography>
                </Flex>
                <Select
                  type="text"
                  name="isBlob"
                  value={filters.isBlob === null ? "null" : filters.isBlob.toString()}
                  size="medium"
                  onChange={(e) =>
                    handleFilterChange("isBlob", e.target.value === "null" ? null : e.target.value === "true")
                  }
                >
                  <MenuItem value="null" size="medium">
                    [A] Blob: All
                  </MenuItem>
                  <MenuItem value="true" size="medium">
                    [A] Blob: True
                  </MenuItem>
                  <MenuItem value="false" size="medium">
                    [A] Blob: False
                  </MenuItem>
                </Select>
                <Select
                  type="text"
                  name="isWrongShoe"
                  value={filters.isWrongShoe === null ? "null" : filters.isWrongShoe.toString()}
                  size="medium"
                  onChange={(e) =>
                    handleFilterChange("isWrongShoe", e.target.value === "null" ? null : e.target.value === "true")
                  }
                >
                  <MenuItem value="null" size="medium">
                    [A] Wrong Shoe: All
                  </MenuItem>
                  <MenuItem value="true" size="medium">
                    [A] Wrong Shoe: True
                  </MenuItem>
                  <MenuItem value="false" size="medium">
                    [A] Wrong Shoe: False
                  </MenuItem>
                </Select>
                <Select
                  type="text"
                  name="isIncoherent"
                  value={filters.isIncoherent === null ? "null" : filters.isIncoherent.toString()}
                  size="medium"
                  onChange={(e) =>
                    handleFilterChange("isIncoherent", e.target.value === "null" ? null : e.target.value === "true")
                  }
                >
                  <MenuItem value="null" size="medium">
                    [A] Incoherent: All
                  </MenuItem>
                  <MenuItem value="true" size="medium">
                    [A] Incoherent: True
                  </MenuItem>
                  <MenuItem value="false" size="medium">
                    [A] Incoherent: False
                  </MenuItem>
                </Select>
                <Select
                  type="text"
                  name="isAcceptedAuto"
                  value={filters.isAcceptedAuto === null ? "null" : filters.isAcceptedAuto.toString()}
                  size="medium"
                  onChange={(e) =>
                    handleFilterChange("isAcceptedAuto", e.target.value === "null" ? null : e.target.value === "true")
                  }
                >
                  <MenuItem value="null" size="medium">
                    [A] Accepted: All
                  </MenuItem>
                  <MenuItem value="true" size="medium">
                    [A] Accepted: True
                  </MenuItem>
                  <MenuItem value="false" size="medium">
                    [A] Accepted: False
                  </MenuItem>
                </Select>
                <Button
                  variant="contained"
                  color="primary"
                  size="small"
                  onClick={() => {
                    setFilters({
                      isBlob: null,
                      isWrongShoe: null,
                      isIncoherent: null,
                      isAcceptedAuto: null,
                      isAcceptedManual: null,
                      isBlobManual: null,
                      isWrongShoeManual: null,
                      isIncoherentManual: null,
                      isBadLacesManual: null,
                      isBadFineDetailsManual: null
                    });
                  }}
                  dataDogIdentifier="experiment creatives page reset all filters"
                >
                  Reset All Filters
                </Button>
              </Flex>

              {/* Second row - Manual filters */}
              <Flex gap="4px" pb="8px">
                <Flex alignItems="center" sx={{ minWidth: "124px" }}>
                  <Typography variant="subtitle1">Manual Filters:</Typography>
                </Flex>
                <Select
                  type="text"
                  name="isAcceptedManual"
                  value={filters.isAcceptedManual === null ? "null" : filters.isAcceptedManual.toString()}
                  size="medium"
                  onChange={(e) =>
                    handleFilterChange("isAcceptedManual", e.target.value === "null" ? null : e.target.value === "true")
                  }
                >
                  <MenuItem value="null" size="medium">
                    [M] Accepted: All
                  </MenuItem>
                  <MenuItem value="true" size="medium">
                    [M] Accepted: True
                  </MenuItem>
                  <MenuItem value="false" size="medium">
                    [M] Accepted: False
                  </MenuItem>
                </Select>
                <Select
                  type="text"
                  name="isBlobManual"
                  value={filters.isBlobManual === null ? "null" : filters.isBlobManual.toString()}
                  size="medium"
                  onChange={(e) =>
                    handleFilterChange("isBlobManual", e.target.value === "null" ? null : e.target.value === "true")
                  }
                >
                  <MenuItem value="null" size="medium">
                    [M] Blob: All
                  </MenuItem>
                  <MenuItem value="true" size="medium">
                    [M] Blob: True
                  </MenuItem>
                  <MenuItem value="false" size="medium">
                    [M] Blob: False
                  </MenuItem>
                </Select>
                <Select
                  type="text"
                  name="isWrongShoeManual"
                  value={filters.isWrongShoeManual === null ? "null" : filters.isWrongShoeManual.toString()}
                  size="medium"
                  onChange={(e) =>
                    handleFilterChange(
                      "isWrongShoeManual",
                      e.target.value === "null" ? null : e.target.value === "true"
                    )
                  }
                >
                  <MenuItem value="null" size="medium">
                    [M] Wrong Shoe: All
                  </MenuItem>
                  <MenuItem value="true" size="medium">
                    [M] Wrong Shoe: True
                  </MenuItem>
                  <MenuItem value="false" size="medium">
                    [M] Wrong Shoe: False
                  </MenuItem>
                </Select>
                <Select
                  type="text"
                  name="isIncoherentManual"
                  value={filters.isIncoherentManual === null ? "null" : filters.isIncoherentManual.toString()}
                  size="medium"
                  onChange={(e) =>
                    handleFilterChange(
                      "isIncoherentManual",
                      e.target.value === "null" ? null : e.target.value === "true"
                    )
                  }
                >
                  <MenuItem value="null" size="medium">
                    [M] Incoherent: All
                  </MenuItem>
                  <MenuItem value="true" size="medium">
                    [M] Incoherent: True
                  </MenuItem>
                  <MenuItem value="false" size="medium">
                    [M] Incoherent: False
                  </MenuItem>
                </Select>
                <Select
                  type="text"
                  name="isBadLacesManual"
                  value={filters.isBadLacesManual === null ? "null" : filters.isBadLacesManual.toString()}
                  size="medium"
                  onChange={(e) =>
                    handleFilterChange("isBadLacesManual", e.target.value === "null" ? null : e.target.value === "true")
                  }
                >
                  <MenuItem value="null" size="medium">
                    [M] Bad Laces: All
                  </MenuItem>
                  <MenuItem value="true" size="medium">
                    [M] Bad Laces: True
                  </MenuItem>
                  <MenuItem value="false" size="medium">
                    [M] Bad Laces: False
                  </MenuItem>
                </Select>
                <Select
                  type="text"
                  name="isBadFineDetailsManual"
                  value={filters.isBadFineDetailsManual === null ? "null" : filters.isBadFineDetailsManual.toString()}
                  size="medium"
                  onChange={(e) =>
                    handleFilterChange(
                      "isBadFineDetailsManual",
                      e.target.value === "null" ? null : e.target.value === "true"
                    )
                  }
                >
                  <MenuItem value="null" size="medium">
                    [M] Fine Details: All
                  </MenuItem>
                  <MenuItem value="true" size="medium">
                    [M] Fine Details: True
                  </MenuItem>
                  <MenuItem value="false" size="medium">
                    [M] Fine Details: False
                  </MenuItem>
                </Select>
                <Switch checked={isLessTitles} onChange={() => setIsLessTitles(!isLessTitles)} size="small" />
              </Flex>
            </div>
          </CommandBarWrapper>
          <Flex>
            <Table
              height="79vh"
              allowFocusOnRows={true}
              stickyHeaders={true}
              tableId="experiment-creatives"
              columns={columns}
              rows={filteredCreatives}
              isLoading={isLoading}
              ignoreFocusChildElements={true}
              dataDogIdentifier="experiment creatives"
            />
          </Flex>
        </>
      )}
    </Page>
  );
};

export default ExperimentCreativesPage;
